import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Title } from '@dropbox/dig-components/typography';
import styles from './white-labeling-app.module.scss';
import AccordionColorPicker from './accordion-color-picker';
import { FormattedMessage } from 'react-intl';
import { Button } from '@dropbox/dig-components/buttons';
import { MessageType, postTo } from 'js/sign-components/common/ts-message';
import {
  whiteLabelingNew,
  WhiteLabelingPropertyIdNew,
} from 'js/sign-components/common/white-labeling-new';
import { useSectionsNew } from './use-sections-new';
import { applyPreset, exportPreset } from './preset-utils';
import type { ColorConfig } from './preset-utils';
import { stylesToOldWhiteLabeling } from 'js/sign-components/common/white-labeling-old';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';

export const WhiteLabelingAppNew = () => {
  const [colors, setColors] = useState(whiteLabelingNew);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [logoUrl, setLogoUrl] = useState<string>();

  const isColorsImportEnabled = useFeatureFlag(
    'sign_core_2026_04_02_white_labeling_colors_import',
  );

  const updateColors = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      postTo(iframeRef.current.contentWindow, {
        type: MessageType.SignerAppDemoWhiteLabelingNew,
        whiteLabeling: colors,
      });
    }
  }, [colors]);

  const updateLogo = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      postTo(iframeRef.current.contentWindow, {
        type: MessageType.SignerAppDemoLogoUrl,
        url: logoUrl ?? '',
      });
    }
  }, [logoUrl]);

  useEffect(() => {
    updateColors();
  }, [updateColors]);

  useEffect(() => {
    updateLogo();
  }, [updateLogo]);

  const updateIframe = () => {
    updateColors();
    updateLogo();
  };

  const refreshIframe = () => {
    setIframeKey(iframeKey + 1);
  };

  const resetColorsToDefault = useCallback(() => {
    setColors({ ...whiteLabelingNew });
  }, []);

  const importColorPreset = useCallback((preset: ColorConfig) => {
    setColors((prev) => applyPreset(preset, prev).styles);
  }, []);

  const exportColorsToJson = useCallback(() => {
    exportPreset(stylesToOldWhiteLabeling(colors));
  }, [colors]);

  const sections = useSectionsNew();

  return (
    <div className={styles.wrapper}>
      <div className={styles.accordionWrapper}>
        <AccordionColorPicker<WhiteLabelingPropertyIdNew>
          getValue={(id) => colors[id]}
          setValue={(id, value) =>
            setColors((prev) => ({ ...prev, [id]: value }))
          }
          sections={sections}
          setLogoUrl={setLogoUrl}
          onImportColorPreset={importColorPreset}
        />
      </div>
      <div
        className={styles.preview}
        data-qa-ref="white-labeling-preview-selection"
      >
        <div className={styles.previewHeader}>
          <Title size="small">
            <FormattedMessage
              id=""
              description="Preview Title"
              defaultMessage="Preview"
            />
          </Title>
          <div className={styles.viewSelectorRow}>
            <Button variant="primary" onClick={refreshIframe}>
              <FormattedMessage
                id=""
                description="Button which restarts the contract from first page"
                defaultMessage="Restart Contract"
              />
            </Button>
            {isColorsImportEnabled && (
              <>
                <Button variant="outline" onClick={resetColorsToDefault}>
                  <FormattedMessage
                    id=""
                    description="Button that resets color pickers to default theme"
                    defaultMessage="Reset colors"
                  />
                </Button>
                <Button variant="outline" onClick={exportColorsToJson}>
                  <FormattedMessage
                    id=""
                    description="Button that exports colors in a json file"
                    defaultMessage="Export colors"
                  />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className={styles.previewComponentWrapper}>
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={'/api/white-labeling-signer-demo'}
            className={styles.signerIframe}
            onLoad={updateIframe}
          />
        </div>
      </div>
    </div>
  );
};
